import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';

export type {
    SitePlaceDetailRow,
    SitePlaceDetailRecord,
    SitePlaceDetailsSearchRequest,
    SitePlaceDetailsSearchResponse
} from './types/companies-place-details.types';

const API_VERSION = 'v2';
const BASE = `${import.meta.env.VITE_DEPOT_API_COMPANIES}/site-place-details`;

const response = {
    /** Server-side paginated search. POST /companies/site-place-details/ */
    async searchPlaceDetails(payload: Record<string, any>): Promise<any> {
        return await api(HTTP_TYPES.POST, `${BASE}/`, payload, undefined, 'application/json', API_VERSION);
    },
    /**
     * Export the place details matching the current filters as CSV. Same
     * endpoint as the search, with `export: true` in the body and a `text/csv`
     * Accept header.
     */
    async exportPlaceDetailsSearchCSV(payload: Record<string, any>): Promise<any> {
        return await api(HTTP_TYPES.POST, `${BASE}/`, payload, undefined, 'application/json', API_VERSION, {
            Accept: 'text/csv'
        });
    },
    /** Fetch the full raw record. GET /companies/site-place-details/:place_id */
    async getPlaceDetail(placeId: string): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${BASE}/${placeId}`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    /**
     * Set (or update) a site's place_id and store its Google place details.
     * POST /company/site-place-details/:place_id
     */
    async setSitePlaceId(payload: {
        company_uuid: string;
        site_uuid: string;
        place_id: string;
        refetch?: boolean;
    }): Promise<any> {
        return await api(
            HTTP_TYPES.POST,
            `${BASE}/place-id`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    /** Re-fetch the record from Google. POST /companies/site-place-details/refresh/:place_id */
    async refreshPlaceDetail(placeId: string): Promise<any> {
        return await api(
            HTTP_TYPES.POST,
            `${BASE}/refresh/${placeId}`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    /**
     * Bulk-refresh stored places from Google, optionally scoped to a company.
     * Streams progress as server-sent events via `onChunk`.
     * POST /companies/site-place-details/refresh
     */
    async refreshAllPlaceDetails(
        payload: { company_uuid?: string },
        onChunk: (chunk: string) => void
    ): Promise<void> {
        await api(
            HTTP_TYPES.POST,
            `${BASE}/refresh`,
            payload,
            undefined,
            'application/json',
            API_VERSION,
            {},
            onChunk
        );
    },
    /**
     * Backfill the company_uuid / site_uuid identifiers onto stored place
     * records. Takes no body and streams progress via `onChunk`.
     * POST /company/site-place-details/backfill-identifiers
     */
    async backfillIdentifiers(onChunk: (chunk: string) => void): Promise<void> {
        await api(
            HTTP_TYPES.POST,
            `${BASE}/backfill-identifiers`,
            undefined,
            undefined,
            'application/json',
            API_VERSION,
            {},
            onChunk
        );
    },
    /**
     * Validate an uploaded CSV of place details. Sends a `multipart/form-data`
     * body and streams validation progress via `onChunk`. Abortable via `signal`.
     * POST /company/site-place-details/validate-csv
     */
    async validateCsv(
        formData: FormData,
        onChunk: (chunk: string) => void,
        signal?: AbortSignal
    ): Promise<void> {
        await api(
            HTTP_TYPES.POST,
            `${BASE}/validate-csv`,
            formData,
            undefined,
            'application/json',
            API_VERSION,
            {},
            onChunk,
            signal
        );
    },
    /**
     * Import an uploaded CSV of place_ids (header `place_id`, one id per row).
     * Sends a `multipart/form-data` body and streams progress via `onChunk`.
     * Abortable via `signal`.
     * POST /company/site-place-details/import-csv
     */
    async importCsv(
        formData: FormData,
        onChunk: (chunk: string) => void,
        signal?: AbortSignal
    ): Promise<void> {
        await api(
            HTTP_TYPES.POST,
            `${BASE}/import-csv`,
            formData,
            undefined,
            'application/json',
            API_VERSION,
            {},
            onChunk,
            signal
        );
    },
    /**
     * Total count of stored places, optionally scoped to a company.
     * GET /companies/site-place-details/count
     */
    async getPlaceDetailsCount(companyUuid?: string): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${BASE}/count`,
            undefined,
            companyUuid ? { company_uuid: companyUuid } : undefined,
            'application/json',
            API_VERSION
        );
    }
};

export default response;
